// LIVE AUCTIONS page: ticking countdowns, dutch buy/cancel, english
// bid / proof with the deposit-needed and held-bid-package modals.
import { api, flows } from "./trade.js";
import { pendingChanges, recordChange } from "./local-changes.js";
import { ensureConnected } from "./connect.js";
import { toast, confirmModal } from "./ui.js";
import { btcShort } from "./collection-bid.js";
import { t } from "../client/i18n.js";

type HeldBid = flows.HeldBid;

// --- Countdowns (server renders the initial text; we keep it ticking) --------

function untilText(target: number, now = Date.now()): string {
  const s = Math.floor((target - now) / 1000);
  if (s <= 0) return t("client.auctions.ended");
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s % 60}s`;
}

const countdowns = [...document.querySelectorAll<HTMLElement>("[data-countdown]")];
if (countdowns.length > 0) {
  setInterval(() => {
    for (const el of countdowns) {
      el.textContent = untilText(new Date(el.dataset.endsAt!).getTime());
    }
  }, 1000);
}

// --- Wallet-gated buttons -----------------------------------------------------

// Wallet-gated buttons reveal before first paint: [data-proof] via CSS on
// html[data-wallet] (base.html) and seller-only CANCEL via the inline script
// under the cards in auctions.html — this module only wires click handlers.

// --- Modals (deposit / held-bid) ----------------------------------------------

const el = (tag: string, className?: string | null, text?: string | null) => {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text != null) n.textContent = text;
  return n;
};

function pageModal(title: string, fill: (body: HTMLElement) => void) {
  const backdrop = el("div", "modal-backdrop");
  const box = el("div", "modal");
  box.addEventListener("click", (e) => e.stopPropagation());
  backdrop.addEventListener("click", () => backdrop.remove());
  box.appendChild(el("h3", null, title));
  const body = el("div", "modal-body");
  fill(body);
  const actions = el("div", "modal-actions");
  const close = el("button", "btn", t("client.auctions.close")) as HTMLButtonElement;
  close.type = "button";
  close.addEventListener("click", () => backdrop.remove());
  actions.appendChild(close);
  box.append(body, actions);
  backdrop.appendChild(box);
  document.body.appendChild(backdrop);
}

function depositModal(deposit: { address: string; neededSats: number }) {
  pageModal(t("client.auctions.deposit_title"), (body) => {
    const p = el("p");
    p.append(t("client.auctions.deposit_body"));
    p.appendChild(el("strong", null, btcShort(deposit.neededSats)));
    p.append(t("client.auctions.deposit_tail"));
    body.appendChild(p);
    body.appendChild(el("p", "mono", deposit.address));
    body.appendChild(
      el(
        "p",
        "action-note",
        t("client.auctions.deposit_note"),
      ),
    );
  });
}

function heldModal(held: HeldBid) {
  pageModal(t("client.auctions.package_title"), (body) => {
    body.appendChild(
      el(
        "p",
        null,
        t("client.auctions.package_body", {
          price: btcShort(held.priceSats),
          time: new Date(held.lockTime * 1000).toLocaleString(),
        }),
      ),
    );
    body.appendChild(el("p", "k", t("client.auctions.parent")));
    body.appendChild(el("p", "mono", `${held.parentTx.slice(0, 120)}…`));
    body.appendChild(el("p", "k", t("client.auctions.child")));
    body.appendChild(el("p", "mono", `${held.childTx.slice(0, 120)}…`));
    const row = el("div", "action-row");
    const copy = el("button", "btn", t("client.auctions.copy_json")) as HTMLButtonElement;
    copy.type = "button";
    copy.addEventListener("click", () => {
      void navigator.clipboard.writeText(JSON.stringify(held, null, 2));
    });
    row.appendChild(copy);
    body.appendChild(row);
  });
}

// --- Actions --------------------------------------------------------------------

let busy = false;
const run = async (btn: HTMLButtonElement, fn: () => Promise<void>) => {
  if (busy) return;
  busy = true;
  const label = btn.textContent;
  btn.textContent = "…";
  btn.disabled = true;
  try {
    await fn();
  } catch (err) {
    toast(String((err as Error)?.message ?? err), "error");
  } finally {
    btn.textContent = label;
    btn.disabled = false;
    busy = false;
  }
};

document.addEventListener("click", (e) => {
  const btn = (e.target as Element).closest<HTMLButtonElement>(
    "[data-buy-dutch], [data-bid-english], [data-proof], [data-cancel-dutch]",
  );
  if (!btn) return;
  const card = btn.closest<HTMLElement>("[data-auction]")!;
  const id = card.dataset.auction!;

  if (btn.hasAttribute("data-buy-dutch")) {
    void run(btn, async () => {
      const acct = await ensureConnected();
      if (!acct) return;
      const a = (await api.dutchApi.list()).find((x) => x.id === id);
      if (!a) throw new Error(t("client.auctions.no_longer_exists"));
      const price = a.currentStep?.priceSats;
      if (price == null) throw new Error(t("client.auctions.not_started"));
      if (!(await confirmModal(t("client.auctions.buy"), [[t("client.auctions.buy_now_for"), btcShort(price)]], t("client.auctions.buy")))) return;
      const txs = await flows.buyDutch(acct, a);
      toast(t("client.auctions.bought", { txs: txs.map((x) => x.name).join(" + ") }), "ok");
      card.remove(); // the auction is consumed by the purchase
      recordChange({ t: "auction-cancelled", id });
    });
  } else if (btn.hasAttribute("data-bid-english")) {
    void run(btn, async () => {
      const acct = await ensureConnected();
      if (!acct) return;
      const a = (await api.englishApi.list()).find((x) => x.id === id);
      if (!a) throw new Error(t("client.auctions.no_longer_exists"));
      const outcome = await flows.bidEnglish(acct, a);
      if (outcome.status === "deposit-needed") {
        depositModal(outcome);
        return;
      }
      if (outcome.status === "pending") {
        toast(t("client.auctions.pending_bid", { price: btcShort(outcome.priceSats) }), "info");
      } else {
        heldModal(outcome.held);
        toast(t("client.auctions.top_bid", { price: btcShort(outcome.held.priceSats) }), "ok");
      }
    });
  } else if (btn.hasAttribute("data-proof")) {
    void run(btn, async () => {
      const acct = await ensureConnected();
      if (!acct) return;
      const proof = await flows.retrieveProof(acct, id);
      if (!proof) {
        toast(t("client.auctions.still_pending"), "info");
        return;
      }
      heldModal(proof);
    });
  } else {
    void run(btn, async () => {
      await flows.cancelOrder("dutch auction", id, card.dataset.seller!);
      toast(t("client.auctions.cancelled"), "ok");
      card.remove();
      recordChange({ t: "auction-cancelled", id });
    });
  }
});

// Drop cards for auctions bought/cancelled locally while this render was cached.
for (const c of pendingChanges()) {
  if (c.t === "auction-cancelled") {
    document.querySelector(`[data-auction="${c.id}"]`)?.remove();
  }
}
